import type { RouteRecord } from 'vite-react-ssg'
import { RootLayout } from '@/App'
import { categories, industries, subCategories } from '@/content/site'
import { products } from '@/content/products'
import { articles } from '@/content/articles'

/**
 * Routes with per-route code-splitting (lazy). The RootLayout shell stays eager;
 * every page loads as its own chunk. `getStaticPaths` stays inline so the SSG
 * build prerenders all dynamic routes.
 */
export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, lazy: () => import('@/pages/Home').then((m) => ({ Component: m.Home })) },
      { path: 'about', lazy: () => import('@/pages/About').then((m) => ({ Component: m.About })) },
      { path: 'products', lazy: () => import('@/pages/ProductsOverview').then((m) => ({ Component: m.ProductsOverview })) },
      {
        path: 'products/:category',
        lazy: () => import('@/pages/CategoryPage').then((m) => ({ Component: m.CategoryPage })),
        getStaticPaths: () => categories.map((c) => `/products/${c.slug}`),
      },
      {
        // Dispatcher: subcategory listing OR product detail.
        path: 'products/:category/:segment',
        lazy: () => import('@/pages/CategorySegment').then((m) => ({ Component: m.CategorySegment })),
        getStaticPaths: () => [
          ...subCategories.map((s) => `/products/${s.category}/${s.slug}`),
          ...products.map((p) => `/products/${p.category}/${p.slug}`),
        ],
      },
      { path: 'industries', lazy: () => import('@/pages/Industries').then((m) => ({ Component: m.Industries })) },
      {
        path: 'industries/:slug',
        lazy: () => import('@/pages/IndustryDetail').then((m) => ({ Component: m.IndustryDetail })),
        getStaticPaths: () => industries.map((i) => `/industries/${i.slug}`),
      },
      { path: 'articles', lazy: () => import('@/pages/Articles').then((m) => ({ Component: m.Articles })) },
      {
        path: 'articles/:slug',
        lazy: () => import('@/pages/Article').then((m) => ({ Component: m.Article })),
        getStaticPaths: () => articles.map((a) => `/articles/${a.slug}`),
      },
      { path: 'faq', lazy: () => import('@/pages/Faq').then((m) => ({ Component: m.Faq })) },
      { path: 'downloads', lazy: () => import('@/pages/Downloads').then((m) => ({ Component: m.Downloads })) },
      { path: 'contact', lazy: () => import('@/pages/Contact').then((m) => ({ Component: m.Contact })) },
      { path: 'privacy', lazy: () => import('@/pages/Privacy').then((m) => ({ Component: m.Privacy })) },
      { path: 'terms', lazy: () => import('@/pages/Terms').then((m) => ({ Component: m.Terms })) },
      // Prerendered to dist/404.html so Vercel serves the styled NotFound (proper 404 status)
      // for unmatched paths instead of its raw platform 404.
      { path: '404', lazy: () => import('@/pages/NotFound').then((m) => ({ Component: m.NotFound })) },
      { path: '*', lazy: () => import('@/pages/NotFound').then((m) => ({ Component: m.NotFound })) },
    ],
  },
]

export default routes
